import CloseIcon from '@mui/icons-material/Close'
import DownloadOutlined from '@mui/icons-material/DownloadOutlined'
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { FileRepositoryModel } from 'src/models/file-repository-model'
import { formatFileSize, formatShortDate } from '../utils/fileHelpers'

interface FilePreviewModalProps {
  open: boolean
  entry: FileRepositoryModel | null
  onClose: () => void
  onDownload: (entry: FileRepositoryModel) => void
  canDownload: boolean
}

export function FilePreviewModal({
  open,
  entry,
  onClose,
  onDownload,
  canDownload,
}: FilePreviewModalProps) {
  const { t } = useTranslation()

  if (!entry) return null

  const isImage = entry.mimeType?.startsWith('image/')
  const isVideo = entry.mimeType === 'video/mp4'

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            flex: 1,
            mr: 2,
          }}
        >
          {entry.name}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {canDownload && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadOutlined />}
              onClick={() => onDownload(entry)}
            >
              {t('fileRepository.context.download')}
            </Button>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {/* Preview area */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0,0,0,0.03)',
            borderRadius: '8px',
            overflow: 'hidden',
            mb: 2,
          }}
        >
          {isImage && entry.url && (
            <img
              src={entry.url}
              alt={entry.name}
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                objectFit: 'contain',
              }}
            />
          )}
          {isVideo && entry.url && (
            <video
              src={entry.url}
              controls
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
              }}
            />
          )}
        </Box>

        {/* File details */}
        <Box
          sx={{
            display: 'flex',
            gap: 3,
            flexWrap: 'wrap',
            color: 'text.secondary',
          }}
        >
          <Typography variant="body2">
            <strong>{t('fileRepository.details.size')}:</strong>{' '}
            {formatFileSize(entry.sizeInBytes)}
          </Typography>
          <Typography variant="body2">
            <strong>{t('fileRepository.details.type')}:</strong>{' '}
            {entry.mimeType}
          </Typography>
          <Typography variant="body2">
            <strong>{t('fileRepository.details.created')}:</strong>{' '}
            {formatShortDate(entry.createdAt)}
          </Typography>
          <Typography variant="body2">
            <strong>{t('fileRepository.details.modified')}:</strong>{' '}
            {formatShortDate(entry.updatedAt)}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
